import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import PageLayout from '../../components/PageLayout';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../config/firebase';
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import { colors, spacing, borderRadius, shadows, typography } from '../../theme/colors';
import {
  FiPlus, FiEdit2, FiTrash2, FiSearch, FiX
} from 'react-icons/fi';
import useDocumentShortcuts from '../../hooks/useDocumentShortcuts';
import usePagePermissions from '../../hooks/usePagePermissions';
import useEscapeClose from '../../hooks/useEscapeClose';

const UNIT_TYPES = ['Count', 'Weight', 'Volume', 'Length', 'Area', 'Time', 'Other'];

const Units = () => {
  const { activeCompany, userProfile } = useAuth();
  const { canEdit, canDelete, canChange } = usePagePermissions('masterData', 'units');
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');

  const [showModal, setShowModal] = useState(false);
  useEscapeClose(showModal, () => setShowModal(false));
  const [editingUnit, setEditingUnit] = useState(null);

  const [formData, setFormData] = useState({
    unitCode: '',
    unitName: '',
    unitType: 'Count',
    description: '',
    isActive: true
  });

  useEffect(() => {
    if (activeCompany?.id) fetchUnits();
  }, [activeCompany]);

  const fetchUnits = async () => {
    setLoading(true);
    try {
      const q = query(
        collection(db, `companies/${activeCompany.id}/units`),
        orderBy('unitCode')
      );
      const snapshot = await getDocs(q);
      setUnits(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error('Error fetching units:', error);
    }
    setLoading(false);
  };

  const handleAdd = () => {
    setFormData({ unitCode: '', unitName: '', unitType: 'Count', description: '', isActive: true });
    setEditingUnit(null);
    setShowModal(true);
  };

  const handleEdit = (unit) => {
    if (!canChange) { alert('You can add new records but cannot change saved ones.'); return; } // Add-only role (2026-11-04)
    setFormData({
      unitCode: unit.unitCode || '',
      unitName: unit.unitName || '',
      unitType: unit.unitType || 'Count',
      description: unit.description || '',
      isActive: unit.isActive !== false
    });
    setEditingUnit(unit);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    if (!formData.unitCode.trim()) { alert('Unit code is required'); return; }
    if (!formData.unitName.trim()) { alert('Unit name is required'); return; }

    const code = formData.unitCode.trim();
    const dup = units.find(u => u.unitCode === code && u.id !== editingUnit?.id);
    if (dup) { alert(`Unit code "${code}" already exists`); return; }

    try {
      const data = {
        unitCode: code,
        unitName: formData.unitName.trim(),
        unitType: formData.unitType,
        description: formData.description.trim(),
        isActive: formData.isActive,
        updatedAt: serverTimestamp()
      };
      if (editingUnit) {
        await updateDoc(doc(db, `companies/${activeCompany.id}/units`, editingUnit.id), data);
      } else {
        data.createdAt = serverTimestamp();
        data.createdBy = userProfile?.uid;
        await addDoc(collection(db, `companies/${activeCompany.id}/units`), data);
      }
      setShowModal(false);
      fetchUnits();
    } catch (error) {
      console.error('Error saving unit:', error);
      alert('Failed to save unit');
    }
  };

  useDocumentShortcuts({
    onNew: handleAdd,
    onSave: handleSave,
    viewMode: showModal ? 'form' : 'list',
    saving: false
  });

  const handleDelete = async (unit) => {
    if (!canDelete) { alert('You do not have permission to delete.'); return; }
    if (!window.confirm(`Delete unit "${unit.unitCode}"?`)) return;
    try {
      await deleteDoc(doc(db, `companies/${activeCompany.id}/units`, unit.id));
      fetchUnits();
    } catch (error) {
      console.error('Error deleting unit:', error);
      alert('Failed to delete unit');
    }
  };

  const filtered = units.filter(u => {
    const s = searchTerm.toLowerCase();
    const matches =
      u.unitCode?.toLowerCase().includes(s) ||
      u.unitName?.toLowerCase().includes(s) ||
      u.description?.toLowerCase().includes(s);
    const matchesType = typeFilter === 'all' || u.unitType === typeFilter;
    return matches && matchesType;
  });

  return (
    <PageLayout pageTitle="Units of Measure" breadcrumb="Master Data / Units">
      <Container>
        <HelpBox>
          <strong>Units of Measure</strong> defines the units (Pcs, Ctn, Box, Kg, Litre etc.)
          that items can be sold or purchased in. Once defined here, you can attach multiple
          units to an item with conversion factors (e.g. 1 Ctn = 24 Pcs) on the item form.
        </HelpBox>

        <HeaderSection>
          <SearchBox>
            <FiSearch />
            <input
              type="text"
              placeholder="Search units..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <ClearButton onClick={() => setSearchTerm('')}><FiX /></ClearButton>
            )}
          </SearchBox>
          <FilterSelect
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="all">All Types</option>
            {UNIT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </FilterSelect>
          {canEdit && (
            <AddButton onClick={handleAdd}>
              <FiPlus /> Add Unit
            </AddButton>
          )}
        </HeaderSection>

        {loading ? (
          <LoadingMessage>Loading units...</LoadingMessage>
        ) : (
          <TableContainer>
            {filtered.length === 0 ? (
              <EmptyMessage>
                {searchTerm || typeFilter !== 'all'
                  ? 'No units match your search.'
                  : 'No units defined yet. Click "Add Unit" to create your first unit.'}
              </EmptyMessage>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>Code</Th>
                    <Th>Name</Th>
                    <Th>Type</Th>
                    <Th>Description</Th>
                    <Th>Status</Th>
                    <Th style={{ textAlign: 'right' }}>Actions</Th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(u => (
                    <Tr key={u.id}>
                      <Td><CodeBadge>{u.unitCode}</CodeBadge></Td>
                      <Td><strong>{u.unitName}</strong></Td>
                      <Td><TypeBadge>{u.unitType || 'Other'}</TypeBadge></Td>
                      <Td style={{ color: colors.text.secondary }}>{u.description || '-'}</Td>
                      <Td>
                        <StatusBadge $active={u.isActive !== false}>
                          {u.isActive !== false ? 'Active' : 'Inactive'}
                        </StatusBadge>
                      </Td>
                      <Td>
                        <ActionButtons>
                          {canChange && (
                            <ActionButton onClick={() => handleEdit(u)} title="Edit"><FiEdit2 /></ActionButton>
                          )}
                          {canDelete && (
                            <ActionButton $danger onClick={() => handleDelete(u)} title="Delete"><FiTrash2 /></ActionButton>
                          )}
                        </ActionButtons>
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            )}
          </TableContainer>
        )}
      </Container>

      {showModal && (
        <ModalOverlay>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h3>{editingUnit ? 'Edit Unit' : 'Add New Unit'}</h3>
              <CloseButton onClick={() => setShowModal(false)}><FiX /></CloseButton>
            </ModalHeader>
            <ModalBody>
              <FormRow>
                <FormGroup>
                  <label>Unit Code *</label>
                  <input
                    type="text"
                    value={formData.unitCode}
                    onChange={(e) => setFormData({ ...formData, unitCode: e.target.value })}
                    placeholder="Pcs, Ctn, Kg..."
                    maxLength={10}
                  />
                </FormGroup>
                <FormGroup>
                  <label>Unit Name *</label>
                  <input
                    type="text"
                    value={formData.unitName}
                    onChange={(e) => setFormData({ ...formData, unitName: e.target.value })}
                    placeholder="Pieces, Carton, Kilogram..."
                  />
                </FormGroup>
              </FormRow>
              <FormRow>
                <FormGroup>
                  <label>Unit Type</label>
                  <select
                    value={formData.unitType}
                    onChange={(e) => setFormData({ ...formData, unitType: e.target.value })}
                  >
                    {UNIT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </FormGroup>
                <FormGroup>
                  <label>Active</label>
                  <select
                    value={formData.isActive ? 'yes' : 'no'}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.value === 'yes' })}
                  >
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </FormGroup>
              </FormRow>
              <FormGroup>
                <label>Description</label>
                <textarea
                  rows={2}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Optional notes about this unit"
                />
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <CancelButton onClick={() => setShowModal(false)}>Cancel</CancelButton>
              {canEdit && (
                <SaveButton onClick={handleSave}>
                  {editingUnit ? 'Update' : 'Save'}
                </SaveButton>
              )}
            </ModalFooter>
          </Modal>
        </ModalOverlay>
      )}
    </PageLayout>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.lg};
`;

const HelpBox = styled.div`
  padding: ${spacing.md};
  background: ${colors.primary.main}08;
  border-left: 3px solid ${colors.primary.main};
  border-radius: ${borderRadius.sm};
  font-size: ${typography.fontSize.sm};
  color: ${colors.text.secondary};
  line-height: 1.5;

  strong { color: ${colors.text.primary}; }
`;

const HeaderSection = styled.div`
  display: flex;
  gap: ${spacing.md};
  align-items: center;
  flex-wrap: wrap;
`;

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  background: ${colors.background.default};
  border: 1px solid ${colors.border.main};
  border-radius: ${borderRadius.md};
  padding: ${spacing.sm} ${spacing.md};
  flex: 1;
  max-width: 300px;

  svg { color: ${colors.text.secondary}; }
  input { border: none; outline: none; flex: 1; font-size: ${typography.fontSize.sm}; background: transparent; }
`;

const ClearButton = styled.button`
  background: transparent;
  color: ${colors.text.secondary};
  padding: 4px;
  display: flex;
  align-items: center;
  border-radius: ${borderRadius.sm};
  &:hover { background: ${colors.background.hover}; }
`;

const FilterSelect = styled.select`
  padding: ${spacing.sm} ${spacing.md};
  border: 1px solid ${colors.border.main};
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSize.sm};
  background: ${colors.background.default};
  cursor: pointer;
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  padding: ${spacing.sm} ${spacing.md};
  background: ${colors.primary.main};
  color: ${colors.text.white};
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  margin-left: auto;

  &:hover { background: ${colors.primary.dark}; }
`;

const TableContainer = styled.div`
  background: ${colors.background.default};
  border-radius: ${borderRadius.md};
  border: 1px solid ${colors.border.light};
  overflow: hidden;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: ${spacing.xl};
  color: ${colors.text.secondary};
  background: ${colors.background.default};
  border-radius: ${borderRadius.md};
  border: 1px solid ${colors.border.light};
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: ${spacing.xl};
  color: ${colors.text.secondary};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: ${spacing.md};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.text.secondary};
  text-transform: uppercase;
  background: ${colors.background.paper};
  border-bottom: 1px solid ${colors.border.light};
`;

const Tr = styled.tr`
  border-bottom: 1px solid ${colors.border.light};
  &:hover { background: ${colors.background.hover}; }
  &:last-child { border-bottom: none; }
`;

const Td = styled.td`
  padding: ${spacing.md};
  vertical-align: middle;
  font-size: ${typography.fontSize.sm};
`;

const CodeBadge = styled.span`
  font-family: monospace;
  font-size: ${typography.fontSize.sm};
  color: ${colors.primary.main};
  background: ${colors.primary.main}10;
  padding: 4px 10px;
  border-radius: ${borderRadius.sm};
  font-weight: ${typography.fontWeight.medium};
`;

const TypeBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: ${borderRadius.sm};
  font-size: ${typography.fontSize.xs};
  background: ${colors.background.paper};
  color: ${colors.text.secondary};
  border: 1px solid ${colors.border.light};
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 2px 10px;
  border-radius: ${borderRadius.sm};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.medium};
  background: ${({ $active }) => ($active ? '#10b98115' : '#9ca3af15')};
  color: ${({ $active }) => ($active ? '#10b981' : '#6b7280')};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${spacing.xs};
  justify-content: flex-end;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  color: ${({ $danger }) => ($danger ? colors.error.main : colors.text.secondary)};
  border-radius: ${borderRadius.sm};

  &:hover {
    background: ${({ $danger }) => ($danger ? colors.error.main + '15' : colors.background.hover)};
    color: ${({ $danger }) => ($danger ? colors.error.main : colors.primary.main)};
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${spacing.md};
`;

const Modal = styled.div`
  background: ${colors.background.default};
  border-radius: ${borderRadius.lg};
  width: 100%;
  max-width: 540px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: ${shadows.xl};
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${spacing.md} ${spacing.lg};
  border-bottom: 1px solid ${colors.border.light};
  h3 { font-size: ${typography.fontSize.lg}; font-weight: ${typography.fontWeight.semibold}; color: ${colors.text.primary}; margin: 0; }
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px; height: 32px;
  background: transparent;
  color: ${colors.text.secondary};
  border-radius: ${borderRadius.sm};
  &:hover { background: ${colors.background.hover}; color: ${colors.text.primary}; }
`;

const ModalBody = styled.div`
  padding: ${spacing.lg};
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${spacing.md};
  @media (max-width: 500px) { grid-template-columns: 1fr; }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  label { font-size: ${typography.fontSize.sm}; font-weight: ${typography.fontWeight.medium}; color: ${colors.text.secondary}; }
  input, select, textarea {
    padding: ${spacing.sm} ${spacing.md};
    border: 1px solid ${colors.border.main};
    border-radius: ${borderRadius.md};
    font-size: ${typography.fontSize.sm};
    color: ${colors.text.primary};
    background: ${colors.background.default};
    &:focus { outline: none; border-color: ${colors.primary.main}; box-shadow: 0 0 0 3px ${colors.primary.main}20; }
  }
  textarea { resize: vertical; }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${spacing.sm};
  padding: ${spacing.md} ${spacing.lg};
  border-top: 1px solid ${colors.border.light};
`;

const CancelButton = styled.button`
  padding: ${spacing.sm} ${spacing.lg};
  background: transparent;
  color: ${colors.text.secondary};
  border: 1px solid ${colors.border.main};
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  &:hover { background: ${colors.background.hover}; border-color: ${colors.text.secondary}; }
`;

const SaveButton = styled.button`
  padding: ${spacing.sm} ${spacing.lg};
  background: ${colors.primary.main};
  color: ${colors.text.white};
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  &:hover { background: ${colors.primary.dark}; }
`;

export default Units;
